// A small, dependency-free Markdown subset covering what coding agents actually
// emit in chat: headings, emphasis, inline code, fenced code, lists, quotes,
// tables, rules, links and images (as links). It is mirrored line-for-line by the Swift parser in
// apps/native/Sources/RivetsCore/Models/MarkdownDocument.swift — keep the two in
// sync so a transcript reads identically on web, desktop and iOS.
//
// Deliberate departures from CommonMark, chosen for chat text:
//   - a single newline inside a paragraph is a hard break (agents wrap by hand)
//   - `_` only opens emphasis at a word boundary, so snake_case survives
//   - raw HTML is never interpreted, only ever shown as text

export type InlineNode =
  | { type: "text"; value: string }
  | { type: "code"; value: string }
  | { type: "strong"; children: InlineNode[] }
  | { type: "em"; children: InlineNode[] }
  | { type: "strike"; children: InlineNode[] }
  | { type: "link"; href: string; children: InlineNode[] }
  | { type: "break" };

export type BlockNode =
  | { type: "paragraph"; children: InlineNode[] }
  | { type: "heading"; level: number; children: InlineNode[] }
  | { type: "code"; language: string | null; value: string }
  | { type: "quote"; children: BlockNode[] }
  | { type: "list"; ordered: boolean; start: number; items: BlockNode[][] }
  | { type: "table"; align: TableAlign[]; header: InlineNode[][]; rows: InlineNode[][][] }
  | { type: "rule" };

export type TableAlign = "left" | "center" | "right";

const FENCE_RE = /^ {0,3}(`{3,}|~{3,})\s*(\S*).*$/;
const HEADING_RE = /^ {0,3}(#{1,6})(?:\s+(.*?))?\s*$/;
const RULE_RE = /^ {0,3}(?:\*[ \t]*){3,}$|^ {0,3}(?:-[ \t]*){3,}$|^ {0,3}(?:_[ \t]*){3,}$/;
const QUOTE_RE = /^ {0,3}>[ \t]?(.*)$/;
const BULLET_RE = /^([ \t]*)([-*+])([ \t]+)(.*)$/;
const ORDERED_RE = /^([ \t]*)(\d{1,9})([.)])([ \t]+)(.*)$/;
const TABLE_DELIMITER_CELL_RE = /^:?-+:?$/;
const LINK_RE = /^\[([^\]]*)\]\(\s*<?([^)<>\s]*)>?(?:\s+"[^"]*")?\s*\)/;
const AUTOLINK_RE = /^https?:\/\/[^\s<>()[\]"'`]+/;

/** Parses Markdown source into the block tree the renderers walk. */
export function parseMarkdown(source: string): BlockNode[] {
  return parseBlocks(source.replace(/\r\n?/g, "\n").split("\n"));
}

function parseBlocks(lines: string[]): BlockNode[] {
  const blocks: BlockNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i++;
      continue;
    }

    const fence = FENCE_RE.exec(line);
    if (fence) {
      const marker = fence[1]!;
      const body: string[] = [];
      i++;
      while (i < lines.length && !isClosingFence(lines[i]!, marker)) {
        body.push(lines[i]!);
        i++;
      }
      // An unterminated fence still renders as code: mid-stream the closing
      // ticks simply have not arrived yet.
      if (i < lines.length) i++;
      blocks.push({ type: "code", language: fence[2] || null, value: body.join("\n") });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const text = (heading[2] ?? "").replace(/\s+#+\s*$/, "");
      blocks.push({ type: "heading", level: heading[1]!.length, children: parseInline(text) });
      i++;
      continue;
    }

    if (RULE_RE.test(line)) {
      blocks.push({ type: "rule" });
      i++;
      continue;
    }

    const quote = QUOTE_RE.exec(line);
    if (quote) {
      const body: string[] = [quote[1]!];
      i++;
      while (i < lines.length) {
        const next = QUOTE_RE.exec(lines[i]!);
        if (next) {
          body.push(next[1]!);
        } else if (lines[i]!.trim() === "") {
          break;
        } else {
          body.push(lines[i]!); // lazy continuation of the quoted paragraph
        }
        i++;
      }
      blocks.push({ type: "quote", children: parseBlocks(body) });
      continue;
    }

    const table = parseTable(lines, i);
    if (table) {
      blocks.push(table.node);
      i = table.next;
      continue;
    }

    if (!RULE_RE.test(line) && matchListItem(line)) {
      const list = parseList(lines, i);
      blocks.push(list.node);
      i = list.next;
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length) {
      const current = lines[i]!;
      if (current.trim() === "") break;
      if (paragraph.length > 0 && (startsNewBlock(current) || startsTable(lines, i))) break;
      paragraph.push(current.trim());
      i++;
    }
    blocks.push({ type: "paragraph", children: parseInline(paragraph.join("\n")) });
  }
  return blocks;
}

/** Splits a message that is still being written into the part that is finished
 * and the part that is not.
 *
 * While an agent streams, the text grows by a few characters every frame and the
 * renderer re-parses all of it, so showing one message costs the square of its
 * length. Everything before the last blank line is settled, so it is parsed once
 * and reused; only the paragraph being written is re-parsed.
 *
 * The split point is chosen so that `parseMarkdown(head)` concatenated with
 * `parseMarkdown(tail)` is exactly `parseMarkdown(source)`: a blank line outside
 * any fence, followed by a line that cannot be a continuation of what came
 * before — no indent (which would continue a list item), no list marker (which
 * would continue a list) and no quote marker. Returns null when the message has
 * no such point yet, in which case the caller parses it whole.
 *
 * Mirrored by `MarkdownDocument.streamingSplit` in the Swift parser. */
export function splitStreamingMarkdown(source: string): { head: string; tail: string } | null {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const cut = settledLineCount(lines);
  if (cut == null || cut <= 0 || cut >= lines.length) return null;
  return { head: lines.slice(0, cut).join("\n"), tail: lines.slice(cut).join("\n") };
}

/** Index of the first line of the unsettled tail, or null when there is none. */
function settledLineCount(lines: string[]): number | null {
  let cut: number | null = null;
  let openFence: string | null = null;
  let sawBlank = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (openFence != null) {
      if (isClosingFence(line, openFence)) openFence = null;
      sawBlank = false;
      continue;
    }
    if (line.trim() === "") {
      sawBlank = true;
      continue;
    }
    // A fence is tracked even when it is indented inside a list item:
    // misreading one only suppresses split points, never invents them.
    const fence = FENCE_RE.exec(line);
    if (fence) {
      openFence = fence[1]!;
      sawBlank = false;
      continue;
    }
    if (sawBlank && startsSettledBlock(line)) cut = i;
    sawBlank = false;
  }
  return cut;
}

/** True when this line begins a block that no preceding block can absorb, so the
 * blank line before it is a safe place to cut. */
function startsSettledBlock(line: string): boolean {
  return !/^[ \t]/.test(line) && matchListItem(line) == null && !QUOTE_RE.test(line);
}

function startsNewBlock(line: string): boolean {
  return (
    FENCE_RE.test(line) ||
    HEADING_RE.test(line) ||
    RULE_RE.test(line) ||
    QUOTE_RE.test(line) ||
    matchListItem(line) != null
  );
}

function isClosingFence(line: string, marker: string): boolean {
  const match = /^ {0,3}(`{3,}|~{3,})\s*$/.exec(line);
  return match != null && match[1]![0] === marker[0] && match[1]!.length >= marker.length;
}

/** True when `lines[index]` is a table header row: it has a pipe and the line
 * after it is a delimiter row with the same number of cells. */
function startsTable(lines: string[], index: number): boolean {
  return tableHeader(lines, index) != null;
}

function tableHeader(lines: string[], index: number): { cells: string[]; align: TableAlign[] } | null {
  const header = lines[index]!;
  const delimiter = lines[index + 1];
  if (delimiter == null || !header.includes("|") || !delimiter.includes("|")) return null;
  const align = delimiterAlignments(delimiter);
  if (!align) return null;
  const cells = splitTableRow(header);
  if (cells.length !== align.length) return null;
  return { cells, align };
}

function delimiterAlignments(line: string): TableAlign[] | null {
  const cells = splitTableRow(line);
  if (cells.length === 0) return null;
  const align: TableAlign[] = [];
  for (const cell of cells) {
    if (!TABLE_DELIMITER_CELL_RE.test(cell)) return null;
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    align.push(left && right ? "center" : right ? "right" : "left");
  }
  return align;
}

/**
 * GitHub-style pipe table. The body runs until a blank line, a line with no
 * pipe, or the start of another block; short rows are padded and long rows
 * trimmed to the header's width.
 */
function parseTable(lines: string[], start: number): { node: BlockNode; next: number } | null {
  const header = tableHeader(lines, start);
  if (!header) return null;
  const columns = header.align.length;
  const rows: InlineNode[][][] = [];
  let i = start + 2;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "" || !line.includes("|") || startsNewBlock(line)) break;
    const cells = splitTableRow(line).slice(0, columns);
    while (cells.length < columns) cells.push("");
    rows.push(cells.map((cell) => parseInline(cell)));
    i++;
  }
  return {
    node: {
      type: "table",
      align: header.align,
      header: header.cells.map((cell) => parseInline(cell)),
      rows,
    },
    next: i,
  };
}

/**
 * Splits a table row on its pipes. A pipe inside a code span stays part of the
 * cell — agents put union types like `string | null` in tables all the time —
 * and `\|` is an escaped, literal pipe. The optional outer pipes are dropped.
 */
function splitTableRow(line: string): string[] {
  const text = line.trim();
  const cells: string[] = [];
  let cell = "";
  let i = 0;
  while (i < text.length) {
    const char = text[i]!;
    if (char === "\\" && text[i + 1] === "|") {
      cell += "|";
      i += 2;
      continue;
    }
    if (char === "`") {
      const run = runLength(text, i, "`");
      const close = findRun(text, i + run, "`", run);
      if (close >= 0) {
        cell += text.slice(i, close + run).replace(/\\\|/g, "|");
        i = close + run;
        continue;
      }
      cell += text.slice(i, i + run);
      i += run;
      continue;
    }
    if (char === "|") {
      cells.push(cell.trim());
      cell = "";
      i++;
      continue;
    }
    cell += char;
    i++;
  }
  cells.push(cell.trim());
  if (text.startsWith("|")) cells.shift();
  if (text.length > 1 && text.endsWith("|") && !text.endsWith("\\|")) cells.pop();
  return cells;
}

interface ListItemMatch {
  indent: number;
  markerWidth: number;
  ordered: boolean;
  number: number;
  content: string;
}

function matchListItem(line: string): ListItemMatch | null {
  const bullet = BULLET_RE.exec(line);
  if (bullet) {
    return {
      indent: bullet[1]!.length,
      markerWidth: bullet[2]!.length + bullet[3]!.length,
      ordered: false,
      number: 1,
      content: bullet[4]!,
    };
  }
  const ordered = ORDERED_RE.exec(line);
  if (ordered) {
    return {
      indent: ordered[1]!.length,
      markerWidth: ordered[2]!.length + ordered[3]!.length + ordered[4]!.length,
      ordered: true,
      number: Number(ordered[2]),
      content: ordered[5]!,
    };
  }
  return null;
}

function parseList(lines: string[], start: number): { node: BlockNode; next: number } {
  const first = matchListItem(lines[start]!)!;
  const ordered = first.ordered;
  const items: string[][] = [];
  let current: string[] = [];
  let contentIndent = first.indent + first.markerWidth;
  let started = false;
  let blanks = 0;
  let i = start;

  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      blanks++;
      i++;
      continue;
    }
    const indent = line.length - line.trimStart().length;
    const item = RULE_RE.test(line) ? null : matchListItem(line);

    if (item && indent < contentIndent) {
      if (started && item.ordered !== ordered) break;
      if (started) items.push(current);
      current = [item.content];
      contentIndent = item.indent + item.markerWidth;
      started = true;
      blanks = 0;
      i++;
      continue;
    }

    if (indent >= contentIndent) {
      for (let b = 0; b < blanks; b++) current.push("");
      blanks = 0;
      current.push(line.slice(contentIndent));
      i++;
      continue;
    }

    // A less-indented line only continues the item when it hugs it; once a
    // blank line intervenes the list is finished.
    if (blanks > 0) break;
    current.push(line.trim());
    i++;
  }

  items.push(current);
  return {
    node: {
      type: "list",
      ordered,
      start: ordered ? first.number : 1,
      items: items.map((item) => parseBlocks(item)),
    },
    next: i,
  };
}

const ESCAPABLE = "\\`*_{}[]()#+-.!>~|";

/** Parses the inline span syntax inside a single block. */
export function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let buffer = "";
  let i = 0;

  const flush = () => {
    if (buffer) {
      nodes.push({ type: "text", value: buffer });
      buffer = "";
    }
  };

  while (i < text.length) {
    const char = text[i]!;

    if (char === "\\" && i + 1 < text.length && ESCAPABLE.includes(text[i + 1]!)) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }

    if (char === "\n") {
      flush();
      nodes.push({ type: "break" });
      i++;
      continue;
    }

    if (char === "`") {
      const run = runLength(text, i, "`");
      const close = findRun(text, i + run, "`", run);
      if (close >= 0) {
        flush();
        let value = text.slice(i + run, close);
        if (value.length > 1 && value.startsWith(" ") && value.endsWith(" ")) value = value.slice(1, -1);
        nodes.push({ type: "code", value });
        i = close + run;
        continue;
      }
    }

    // An image is drawn as a link to it: an agent that saves a screenshot
    // embeds it by path, and in chat the picture opens on demand instead.
    const image = char === "!" && text[i + 1] === "[";
    if (char === "[" || image) {
      const link = LINK_RE.exec(text.slice(image ? i + 1 : i));
      if (link) {
        const href = safeHref(link[2]!);
        flush();
        // An image's alt text is often empty, and a link needs a label.
        const children = parseInline(image && !link[1]!.trim() ? link[2]! : link[1]!);
        // A rejected destination still shows its label, just not as a link.
        if (href) nodes.push({ type: "link", href, children });
        else nodes.push(...children);
        i += link[0].length + (image ? 1 : 0);
        continue;
      }
    }

    if (char === "h") {
      const auto = AUTOLINK_RE.exec(text.slice(i));
      if (auto) {
        const url = auto[0].replace(/[.,;:!?]+$/, "");
        flush();
        nodes.push({ type: "link", href: url, children: [{ type: "text", value: url }] });
        i += url.length;
        continue;
      }
    }

    if (char === "~" && runLength(text, i, "~") >= 2 && opensSpan(text, i)) {
      const close = findRun(text, i + 2, "~", 2);
      if (close > i + 2) {
        flush();
        nodes.push({ type: "strike", children: parseInline(text.slice(i + 2, close)) });
        i = close + 2;
        continue;
      }
    }

    if (char === "*" || char === "_") {
      const emphasis = readEmphasis(text, i, char);
      if (emphasis) {
        flush();
        nodes.push(emphasis.node);
        i = emphasis.next;
        continue;
      }
    }

    buffer += char;
    i++;
  }

  flush();
  return nodes;
}

function readEmphasis(text: string, index: number, delim: string): { node: InlineNode; next: number } | null {
  const run = Math.min(runLength(text, index, delim), 3);
  if (!opensSpan(text, index)) return null;
  const contentStart = index + run;
  if (contentStart >= text.length || /\s/.test(text[contentStart]!)) return null;

  // Walk candidate closers so a nested run (`*a **b** c*`) does not abort the
  // outer span the moment the first candidate turns out to be preceded by space.
  let close = findRun(text, contentStart, delim, run);
  while (close >= 0) {
    const trailingSpace = /\s/.test(text[close - 1]!);
    const intraword = delim === "_" && close + run < text.length && isWordChar(text[close + run]!);
    if (close !== contentStart && !trailingSpace && !intraword) break;
    close = findRun(text, close + runLength(text, close, delim), delim, run);
  }
  if (close < 0) return null;

  const children = parseInline(text.slice(contentStart, close));
  const node: InlineNode =
    run >= 3
      ? { type: "em", children: [{ type: "strong", children }] }
      : run === 2
        ? { type: "strong", children }
        : { type: "em", children };
  return { node, next: close + run };
}

function isWordChar(char: string): boolean {
  return /[\w]/.test(char);
}

/**
 * Emphasis may only open at a word boundary — the start of the text, after
 * whitespace, or after an opening bracket or quote. CommonMark is looser, but
 * agents write globs (`src/**\/*.ts`), arithmetic (`2*3*4`) and identifiers
 * (`some_var_name`) constantly, and none of those should turn into italics.
 */
function opensSpan(text: string, index: number): boolean {
  if (index === 0) return true;
  const previous = text[index - 1]!;
  return /\s/.test(previous) || "([{<\"'".includes(previous);
}

function runLength(text: string, index: number, char: string): number {
  let length = 0;
  while (index + length < text.length && text[index + length] === char) length++;
  return length;
}

/**
 * Finds the next unescaped run of exactly `length` `char`s at or after `from`.
 * Requiring an exact length is what lets `*a **b** c*` nest correctly: the inner
 * `**` runs are skipped while looking for the outer single `*`.
 */
function findRun(text: string, from: number, char: string, length: number): number {
  let i = from;
  while (i < text.length) {
    if (text[i] === "\\") {
      i += 2;
      continue;
    }
    if (text[i] === char) {
      const run = runLength(text, i, char);
      if (run === length) return i;
      i += run;
      continue;
    }
    i++;
  }
  return -1;
}

/** Only linkifies schemes that are safe to hand to the browser. */
export function safeHref(href: string): string | null {
  const value = href.trim();
  if (!value) return null;
  if (/^(https?:|mailto:|tel:)/i.test(value)) return value;
  if (isBareFileReference(value)) return value;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  return value;
}

/**
 * `README.md:12` reads like a URL with a `README.md:` scheme. A name with a dot
 * followed by nothing but line numbers is a file an agent cited instead; no real
 * scheme is shaped like that.
 */
export function isBareFileReference(value: string): boolean {
  return /^[^\s:/?#]*\.[^\s:/?#]+:\d+(?:[:-]\d+)*$/.test(value);
}
